/**
 * Dadverse motion loader — db-motion-loader.js (deferred, ~2 KB)
 * Decides IF and WHEN heavy motion code loads. Nothing here animates.
 *
 * - reduced mode: loads nothing.
 * - lite/full: after the window `load` event (or idle), if any section on the
 *   page has data-motion-level other than "none", loads GSAP core,
 *   ScrollTrigger and db-motion-core.js, then db-scroll-scenes.js when a
 *   cinematic section exists.
 * - full only: loads the Three.js portal bundle when the portal story is
 *   within 600px of the viewport, then hands it to the motion core.
 * - page transitions: loaded on idle when the theme setting is on.
 */
(() => {
  const DB = window.DB;
  if (!DB || !DB.settings) return;
  const A = DB.settings.assets || {};
  const html = document.documentElement;
  const mode = () => html.getAttribute('data-db-motion') || 'reduced';
  const pending = new Map();

  const loadScript = (url) => {
    if (!url) return Promise.reject(new Error('missing asset url'));
    if (pending.has(url)) return pending.get(url);
    const p = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = () => resolve(url);
      s.onerror = () => reject(new Error(`Failed to load ${url}`));
      document.head.appendChild(s);
    });
    pending.set(url, p);
    return p;
  };

  const animatedRoots = () =>
    [...document.querySelectorAll('[data-db-section]')].filter((el) => el.dataset.motionLevel && el.dataset.motionLevel !== 'none');
  const cinematicRoots = () => animatedRoots().filter((el) => el.dataset.motionLevel === 'cinematic');

  let corePromise = null;
  const ensureCore = () => {
    if (corePromise) return corePromise;
    corePromise = loadScript(A.gsap)
      .then(() => loadScript(A.scrollTrigger))
      .then(() => loadScript(A.motionCore))
      .then(() => {
        if (!DB.motionCore) throw new Error('motion core missing');
        return DB.motionCore;
      })
      .catch((error) => {
        console.warn('[DB] motion unavailable, staying static', error);
        corePromise = null;
        return null;
      });
    return corePromise;
  };

  let scenesPromise = null;
  const ensureScenes = () =>
    scenesPromise || (scenesPromise = ensureCore().then((core) => (core ? loadScript(A.scrollScenes).catch(() => null) : null)));

  let portalPromise = null;
  const ensurePortal = () =>
    portalPromise || (portalPromise = ensureScenes().then(() => loadScript(A.threePortal).then(() => window.DBPortal || null).catch(() => null)));

  const watchPortal = () => {
    if (mode() !== 'full' || !('IntersectionObserver' in window)) return;
    const roots = [...document.querySelectorAll('[data-db-section="portal-story"][data-motion-level="cinematic"]')];
    if (!roots.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          ensurePortal().then((DBPortal) => {
            if (DBPortal && DB.motionCore && DB.motionCore.attachPortal) DB.motionCore.attachPortal(entry.target, DBPortal);
          });
        });
      },
      { rootMargin: '600px 0px' }
    );
    roots.forEach((r) => io.observe(r));
  };

  const boot = () => {
    if (mode() === 'reduced') return;
    if (!animatedRoots().length) return;
    ensureCore().then((core) => {
      if (!core) return;
      core.boot();
      if (cinematicRoots().length) ensureScenes().then(() => core.refresh());
      watchPortal();
    });
    if (DB.settings.pageTransitions) {
      DB.util.onIdle(() => ensureCore().then((core) => core && loadScript(A.pageTransition).catch(() => null)), 3000);
    }
  };

  const start = () => DB.util.onIdle(boot, 2500);
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });

  // Theme editor: newly loaded sections may need motion even if none did before.
  document.addEventListener('shopify:section:load', (event) => {
    if (mode() === 'reduced') return;
    const root = event.target.querySelector('[data-db-section]');
    if (!root || root.dataset.motionLevel === 'none') return;
    ensureCore().then((core) => {
      if (!core) return;
      core.boot();
      if (root.dataset.motionLevel === 'cinematic') ensureScenes().then(() => core.refresh());
      if (root.dataset.dbSection === 'portal-story') watchPortal();
    });
  });
})();
